import { formatDistanceToNow } from "date-fns";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  getNudgeIcon,
  humaniseCategory,
} from "@/constants/notificationCategories";
import type { Nudge, NudgeCategoryMeta } from "@/types/notifications";

/**
 * One row in the nudges feed.
 *
 * Shared rather than page-local because the sidebar bell will show the same
 * rows in a preview, and because this already near-duplicates the chat list
 * row — a second inline copy is how those two drift apart.
 *
 * The category label and icon come from the backend registry, passed in as
 * `categoryMeta`. A category this build has never heard of still renders — a
 * humanised key and a neutral bell — which is what lets the backend add
 * categories without a Console release. Severity is deliberately not styled:
 * everything ships `info` until a real feed shows what deserves to be loud.
 */

const initials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("") || "?";

const relativeTime = (iso: string) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return formatDistanceToNow(date, { addSuffix: true });
};

export interface NotificationRowProps {
  notification: Nudge;
  onOpen: (notification: Nudge) => void;
  /** From the backend registry. Absent for a category this build doesn't know. */
  categoryMeta?: NudgeCategoryMeta;
  className?: string;
}

export const NotificationRow = ({
  notification,
  onOpen,
  categoryMeta,
  className,
}: NotificationRowProps) => {
  const isUnread = notification.read_at === null;
  const categoryLabel =
    categoryMeta?.label ?? humaniseCategory(notification.category);
  const CategoryIcon = getNudgeIcon(categoryMeta?.icon);
  const timestamp = relativeTime(notification.last_event_at);

  // Chat nudges carry an empty title and lead with the sender; event nudges
  // carry the headline in `title` and often have no actor at all.
  const headline =
    notification.title || notification.actor?.display_name || categoryLabel;

  return (
    <button
      type="button"
      onClick={() => onOpen(notification)}
      // The visual unread cues are a dot and a weight change, neither of which
      // a screen reader can see, so the state goes in the accessible name.
      aria-label={[
        isUnread ? "Unread." : null,
        headline,
        notification.preview || null,
        timestamp,
      ]
        .filter(Boolean)
        .join(" ")}
      className={cn(
        "flex w-full items-start gap-3 px-4 py-3 text-left transition-colors",
        "border-b border-border/60 last:border-b-0",
        "hover:bg-muted/50",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset",
        isUnread && "bg-primary/5 hover:bg-primary/10",
        className
      )}
    >
      {/* Keeps its column when read, so rows stay aligned. */}
      <span
        aria-hidden="true"
        className="flex h-9 w-2 shrink-0 items-center justify-center"
      >
        {isUnread ? <span className="h-2 w-2 rounded-full bg-primary" /> : null}
      </span>

      {notification.actor ? (
        <Avatar className="h-9 w-9 shrink-0">
          {notification.actor.avatar_url ? (
            <AvatarImage src={notification.actor.avatar_url} alt="" />
          ) : null}
          <AvatarFallback>
            {initials(notification.actor.display_name)}
          </AvatarFallback>
        </Avatar>
      ) : (
        // System-generated nudges have no person behind them, so the category
        // stands in for the face rather than showing a blank avatar.
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <CategoryIcon className="size-4" />
        </span>
      )}

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "z-body-md truncate text-foreground",
              isUnread && "font-medium"
            )}
          >
            {headline}
          </span>

          <Badge variant="neutral" className="shrink-0">
            {categoryLabel}
          </Badge>

          {notification.unread_count > 1 ? (
            <Badge className="shrink-0 tabular-nums">
              {notification.unread_count} new
            </Badge>
          ) : null}

          <span className="z-caption ml-auto shrink-0 text-muted-foreground">
            {timestamp}
          </span>
        </div>

        {/* Always empty on a patient-addressed nudge, and for attachments. */}
        {notification.preview ? (
          <p className="z-body truncate text-muted-foreground">
            {notification.preview}
          </p>
        ) : null}
      </div>
    </button>
  );
};
